import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ArrowLeft, CalendarDays, Loader2, Shield } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { fetchOutlookCalendarEvents, initiateOutlookCalendarConnection } from '@/lib/api'

const OutlookCalendarConnect = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [isConnecting, setIsConnecting] = useState(false)
  const [isLoadingEvents, setIsLoadingEvents] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [events, setEvents] = useState<any[]>([])

  const currentMonthRange = useMemo(() => {
    const now = new Date()
    const start = new Date(Date.UTC(now.getFullYear(), now.getMonth(), 1, 0, 0, 0))
    const end = new Date(Date.UTC(now.getFullYear(), now.getMonth() + 1, 1, 0, 0, 0))

    return {
      start: start.toISOString(),
      end: end.toISOString(),
    }
  }, [])

  const loadEvents = useCallback(async () => {
    setIsLoadingEvents(true)
    setError(null)

    try {
      const data = await fetchOutlookCalendarEvents(currentMonthRange)
      setEvents(data.events || [])
    } catch (err: any) {
      console.error('Failed to fetch Outlook Calendar events:', err)
      setEvents([])
      setError(err.message || 'Failed to fetch Outlook Calendar events')
    } finally {
      setIsLoadingEvents(false)
    }
  }, [currentMonthRange])

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search)
    const shouldLoadEvents = queryParams.get('loadEvents') === '1'

    if (shouldLoadEvents) {
      loadEvents()
    }
  }, [loadEvents, location.search])

  const handleConnect = async () => {
    setIsConnecting(true)
    setError(null)

    try {
      await initiateOutlookCalendarConnection()
    } catch (err: any) {
      console.error('Failed to initiate Outlook Calendar connection:', err)
      setError(err.message || 'Failed to connect Outlook Calendar')
      setIsConnecting(false)
    }
  }

  return (
    <div className="w-full max-w-4xl mx-auto pb-4">
      <div className="mb-4 md:mb-6 flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/calendar-setup')}
          className="text-gray-600"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Calendar Setup
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            Connect Microsoft Outlook Calendar
          </h1>
          <p className="text-gray-600">
            Start the Outlook OAuth flow and fetch calendar events for local testing
          </p>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card variant="dark" className="overflow-hidden">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="bg-[#0078D4] p-4 rounded-full">
                <CalendarDays className="h-8 w-8 text-[#FFFFFF]" />
              </div>
            </div>
            <CardTitle className="text-foreground text-xl">Test Outlook OAuth</CardTitle>
            <CardDescription className="text-gray-600">
              Connect Outlook, save the token, then fetch calendar events for the current month.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-sm text-gray-700 space-y-2">
              <p>Expected callback:</p>
              <p className="font-mono text-xs bg-black/20 rounded-lg px-3 py-2 break-all">
                http://localhost:4196/outlook/callback
              </p>
              <p className="text-xs text-gray-600">
                Outlook should redirect back to the frontend page above, which will then send the
                authorization code to the backend for token exchange and saving.
              </p>
            </div>

            {error ? (
              <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            ) : null}

            <Button
              onClick={handleConnect}
              disabled={isConnecting}
              variant="lime"
              className="w-full font-medium"
              size="lg"
            >
              {isConnecting ? 'Getting redirect URL...' : 'Connect Outlook Calendar'}
            </Button>

            <Button
              onClick={loadEvents}
              disabled={isLoadingEvents}
              variant="outline"
              className="w-full border-foreground/10 text-foreground hover:bg-foreground/10"
              size="lg"
            >
              {isLoadingEvents ? 'Loading events...' : 'Load Outlook events'}
            </Button>
          </CardContent>
        </Card>

        <Card variant="dark" className="overflow-hidden">
          <CardHeader>
            <CardTitle className="text-foreground">Current month events</CardTitle>
            <CardDescription className="text-gray-600">
              Outlook events returned from `/api/athlete/calendar/outlook/events`
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="pt-0 border-foreground/10 flex items-start gap-3">
              <Shield className="h-5 w-5 text-[#4d7c0f] shrink-0 mt-0.5" />
              <p className="text-sm text-gray-600">
                This test page helps verify the entire delegated Outlook Calendar read flow before
                we mix it into the main athlete calendar experience.
              </p>
            </div>

            {isLoadingEvents ? (
              <div className="rounded-xl border border-foreground/10 bg-black/20 px-3 py-4 text-sm text-gray-700 flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading Outlook events...
              </div>
            ) : events.length === 0 ? (
              <div className="rounded-xl border border-foreground/10 bg-black/20 px-3 py-4 text-sm text-gray-600">
                No Outlook events loaded yet.
              </div>
            ) : (
              <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                {events.map((event) => (
                  <div
                    key={event.id}
                    className="rounded-xl border border-foreground/10 bg-black/20 px-3.5 py-3"
                  >
                    <p className="text-sm font-medium text-foreground">{event.title}</p>
                    <p className="text-xs text-gray-600 mt-1">
                      {event.allDay ? 'All day' : event.start}
                      {event.end ? ` → ${event.end}` : ''}
                    </p>
                    {event.location ? (
                      <p className="text-xs text-gray-500 mt-1">{event.location}</p>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default OutlookCalendarConnect
